// utils.js
import _ from "lodash-es"
import { nanoid } from "nanoid"

import { BusinessEntityType, InstallationType, TaskStatus } from "../utils/constants.js"

/* An approximation of a PHP function that inverts key/value pairs so array_flip({1: 'a', 2: 'b'}) = {a: 1, b: 2}*/
export const arrayFlip = function (arr) {
    return Object.keys(arr).reduce((accumulator, key) => {
        accumulator[arr[key]] = key
        return accumulator;
    }, {})
}

export const isEmptyObject = (obj) => {
    return Object.keys(obj).length === 0 
    && obj.constructor === Object;
};

export const compareObjects = (obj1, obj2) => {
    try {
        if (obj1 === obj2) {
            return true; // for primatives including null/undefined, or both point to the same object
        }
        if (typeof obj1 !== 'object' || typeof obj2 !== 'object') {
            return obj1 === obj2; // compare non-object types directly
        }
        const keys1 = Object.keys(obj1);
        const keys2 = Object.keys(obj2);
        if (keys1.length !== keys2.length) {
            return false;
        }
        for (let key of keys1) {
            if (!compareObjects(obj1[key], obj2[key])) {
                return false;
            }
        }
        return true;
    }
    catch (error) {
        console.error("utils.compareObjects Exception: ", error.message);
        return false;
    }
}

export const scrollToTop = (id) => {
    //console.log("Scrolling to top of page...")
    if (id) {
        document.getElementById(id).scrollTop = 0
        return
    }
    window.scrollTo({ top: 0, behavior: 'smooth' })
}

// compares two utg string versions and returns -1, 0, 1
// e.g. (5.0.0.3200, 5.0.0.3130) -> returns 1
// e.g. (5.0.0.3130, 5.0.0.3200) -> returns -1
// e.g. (5.0.0.3200, 5.0.0.3200) -> returns 0
export const compareUtgVersions = (utg1, utg2) => {
    try {
        const utg1VersionSections = utg1.split('.')
        const utg2VersionSections = utg2.split('.')
        if (utg1VersionSections.length != utg2VersionSections.length) {
            throw new Error('utg sections do not have same length'); // return immediately if we don't have the same number of sections
        }
        for (let i = 0; i < utg1VersionSections.length; i++) {
            const v1 = parseInt(utg1VersionSections[i], 10)
            const v2 = parseInt(utg2VersionSections[i], 10)
            if (v1 < v2) {
                return -1
            } else if (v1 > v2) {
                return 1
            }
        }
        return 0
    } catch (error) {
        console.log(error)
        throw error
    }
}

// expect queryParameters to be an array of arrays. The inner array should have two items. Index 0 is the key and index 1 is the value.
export const buildUrlWithQueryParameters = (baseUrl, queryParameters) => {
    const queryString = queryParameters.map((item, index) => {
        return `${index == 0 ? '?' : '&'}${item[0]}=${item[1]}`
    })
    return `${baseUrl}${queryString.join('')}`
}

export const checkApiInterfaceConflicts = (apiInterfaces) => {
    //console.log("checkApiInterfaceConflicts: received the following API interfaces")
    //console.log(apiInterfaces)
    let existingPorts = apiInterfaces.map((apiInterface) => apiInterface.listenPort)
    return checkDuplicatesInArray(existingPorts)
}

export const checkDeviceDescriptionConflicts = (devices) => {
    let existingTaskDesc = devices.map((device) => device.taskDesc)
    return checkDuplicatesInArray(existingTaskDesc)
}

export const checkDuplicatesInArray = (array) => {
    const set = new Set()
    for (let i = 0; i < array.length; i++) {
        if (set.has(array[i])) {
            //console.log(`Found a duplicate item ${array[i]} in the following array:`)
            //console.log(array)
            return true
        }
        set.add(array[i])
    }
    return false
}

export const generateJsonFromSchema = (schema) => {

    const generate = (schema) => {
        const result = {};

        if (schema.type === 'object' && schema.properties) {
            for (const key in schema.properties) {
                const property = schema.properties[key];
                if (property.default !== undefined) {
                    result[key] = property.default;
                } else if (property.type === 'object') {
                    result[key] = generate(property);
                }
            }
        }

        return result;
    }

    const jsonData = generate(schema)

    // make sure we have apiInterfaceConfig and deviceConfig objects and child arrays
    jsonData.config.apiInterfaceConfig = jsonData.config.apiInterfaceConfig || {}
    jsonData.config.apiInterfaceConfig.apiInterface = jsonData.config.apiInterfaceConfig.apiInterface || []

    jsonData.config.deviceConfig = jsonData.config.deviceConfig || {}
    jsonData.config.deviceConfig.device = jsonData.config.deviceConfig.device || []

    // api interfaces need an id assigned
    let apiInterfaces = jsonData.config.apiInterfaceConfig.apiInterface
    apiInterfaces.map((apiInterface) => {
        apiInterface.ccid = apiInterface.ccid ? apiInterface.ccid : nanoid()
    })

    return jsonData
}

/**
 * Return a string to be used in the body of an REST API request that accepts filters. 
 * Builds correct structure and applies formatting required by the REST API.
 * @param {Array} filter : Array of filters to be applied to request
 */
export const formatFilterRequestBody = (filter) => {
    // The REST API requires that the filter payload package be encoded as a string with special characters escaped or removed.
    // Rules:
    // Surrounding quotes ""
    // Escape in-line quotes \"
    // Escape in-line backslash \\
    // And remove newlines
    const filterObject = `{"Predicates": ${JSON.stringify(filter)}}` // manually build Predicates
    const formattedFilterObject = filterObject.replace(/"/g, '\\"')
    return `"${formattedFilterObject}"`
}

export const convertUrlReactToEmber = function (config, urlString = null) {
    let print = false
    if (urlString == null) {
        urlString = window.location.href
    }
    const url = new URL(urlString)
    const splat = url.pathname.split('/')
    const directory = splat.length && splat.length > 3 && splat[3] == "dashboard" ? "/dashboard" : splat.join('/')
    const origin = config && config.in4mBaseUrl ? config.in4mBaseUrl : "https://".concat(url.hostname).concat(url.port ? ":".concat(url.port) : "")
    const newUrlString = origin.concat("/legacy/#").concat(directory).concat(url.searchParams.size > 0 ? "?".concat(url.searchParams.toString()) : "")
    if (print) {
        console.log("Converted react url ".concat(urlString).concat(" to ember url ").concat(newUrlString))
    }
    return newUrlString
}

export const iconForOrgType = function(businessEntity){
    if (businessEntity) {
        if (businessEntity.isMerchant) {
            return 'user'
        }
        else if (businessEntity.isResellerOrganization) {
            return 'briefcase'
        }
        else if (businessEntity.isOrganization) {
            return 'globe-americas'
        }
    }
    return null
}

export const isPreactSignal = function(val){
    return typeof val === 'object' && val !== null && 'value' in val
}

export const isPromise = (value) => {
    return (
        value !== null &&
        (typeof value === 'object' || typeof value === 'function') &&
        typeof value.then === 'function'
    );
}

export const hasPermission = (userPermissions, key, action) => {
    const permissions = userPermissions.find(element => element.key == key)
    if (permissions) {
        const permit = permissions[action]
        return permit
    }
    return false
}

/* Parses an arbitrary string value. Used for retrieving data from localStorage, where values are serialized in a form that makes primitive types except for null unreadable by JSON.parse() */
export const parseString = (str) => {
    if (str == null) {
        return null
    }
    else if (typeof str != 'string') {
        throw new Error("parseString(): invalid input parameter of type ".concat(typeof str))
    }
    try {
        return JSON.parse(str)
    }
    catch {
        if (/^(-?\d+)?\.\d+$/.test(str)) {
            if (/^-?\d+$/.test(str)) {
                return parseInt(str)
            }
            return parseFloat(str)
        }
        else if (str == "true") {
            return true
        }
        else if (str == "false") {
            return false
        }
        else if (str == "null") { //might be unnecessary
            return null
        }
        return str
    }
}

export const makeFieldsReadonly = (schema) => {
  if (typeof schema !== "object" || schema === null) return schema

  // Deep copy to avoid mutating the original schema
  const readonlyFieldsSchema = Array.isArray(schema) ? [] : {}

  for (const key in schema) {
    const value = schema[key]

    if (key === "properties" && schema.type === "object") {
      readonlyFieldsSchema[key] = {}
      for (const propName in value) {
        const prop = value[propName]

        if (prop.type === "object") {
          readonlyFieldsSchema[key][propName] = makeFieldsReadonly(prop)
        } else {
          readonlyFieldsSchema[key][propName] = {
            ...prop,
            "readonly": true
          }
        }
      }
    } else {
      readonlyFieldsSchema[key] = makeFieldsReadonly(value)
    }
  }

  return readonlyFieldsSchema
}

export const isset = function(val){
    return typeof val !== 'undefined' && val != null && val != ''
}

export const elementExists = function(id) {
    const element = document.getElementById(id);
    return typeof (element) != 'undefined' && element != null;
}

export const getUserStatusText = (user) => {
    if (user.isInitialUser && user.isActive) {
        return 'New';
    } else if (!user.isActive) {
        return 'Disabled';
    } else if (user.isLocked) {
        return 'Locked';
    } else if (user.isReset) {
        return 'Reset';
    }
    return 'Active';
}

export const isUpperCase = (char) => {
    return typeof (char) == 'string' && char === char.toUpperCase()
}

export const isLowerCase = (char) => {
    return typeof (char) == 'string' && char === char.toLowerCase()
}

export const getInstallationTypeString = (type) => {
    switch (type) {
        case InstallationType.New:
            return "New"
        case InstallationType.Upgrade:
            return "Upgrade"
        case InstallationType.Existing:
            return "Existing"
        case InstallationType.Reinstall:
            return "Reinstall"
        default:
            return "Error"
    }
}

export const defaultTransformer = async (response) => {
    return await response.json()
}

/**
 * Clone an object minus the given properties
 * @param {object} obj
 * @param {Array} abridgements
 */
export const abridgedClone = (obj, abridgements = []) => {
    abridgements = new Set(abridgements)
    let clone = {}
    Object.keys(obj).forEach((key) => {
        if (!abridgements.has(key)) {
            clone[key] = obj[key]
        }
    })
    console.log("Returning the following abridged clone:")
    console.log(clone)
    return clone
}

export const getTabs = function (tabCount) {
    let tabs = ""
    for (let i = 0; i < tabCount; i++) {
        tabs = tabs.concat("\t")
    }
    return tabs
}

/*
Replacement function for JSON.stringify that doesn't throw an error on cyclic object references, and is safe to use on proxies without infinite recursion.
Set the options.collapseArrays to true to abbreviate arrays.
*/
export const stringifyRecursive = function (obj, options = { collapseArrays: false, disallowCycles: false, showTabs: true }) {
    let print = false
    if (print) {
        console.log("Stringifying the following object:")
        console.log(obj)
        console.trace()
    }
    const visited = new WeakSet()
    function recursive(node, tabCount = 1) {
        if (typeof node !== 'object' || node == null) {
            return JSON.stringify(node)
        }
        else if (visited.has(node)) {
            if (options.disallowCycles) {
                throw new Error("Cyclic object reference detected")
            }
            return "<Cyclic object reference>"
        }
        let bracket1
        let bracket2
        const isArray = Array.isArray(node)
        if (isArray) {
            if (options.collapseArrays) {
                return "<Array of length ".concat(node.length).concat(">")
            }
            bracket1 = "["
            bracket2 = "]"
        }
        else {
            bracket1 = "{"
            bracket2 = "}"
        }
        visited.add(node)
        let str = ""
        let nonempty = false
        let tabs = getTabs(tabCount)
        for (const key in node) {
            if (Object.prototype.hasOwnProperty.call(node, key)) {
                if (str !== "") {
                    str = str.concat(",")
                }
                nonempty = true
                if (options.showTabs) {
                    str = str.concat("\n").concat(tabs)
                }
                if (!isArray) {
                    str = str.concat(key).concat(": ")
                }
                str = str.concat(recursive(node[key], tabCount + 1))
            }
        }
        if (nonempty && options.showTabs) {
            str = str.concat("\n").concat(getTabs(tabCount - 1))
        }
        return bracket1.concat(str).concat(bracket2)
    }
    return recursive(obj)
}

export const canDeleteTask = (taskStatus) => {
    switch (taskStatus) {
        case TaskStatus.Requested:
        case TaskStatus.Accepted:
        case TaskStatus.Planned:
            return true
        case TaskStatus.Rejected:
        case TaskStatus.ControllerFailed:
        case TaskStatus.Scheduled:
        case TaskStatus.Completed:
        case TaskStatus.Failed:
        default:
            return false
    }
}

export const getBusinessEntityTypeName = (type) => {
    switch (type) {
        case BusinessEntityType.Organization:
            return "Organization"
        case BusinessEntityType.Merchant:
            return "Merchant"
        case BusinessEntityType.Reseller:
            return "Reseller"
        case BusinessEntityType.Unknown:
        default:
            return "Unknown"
    }
}

export const parseMeasurementString = (value) => {
    const f = "parseMeasurementString()"
    let print = false
    let measurement = []
    let suffix = []
    let float = false
    for (let i = 0; i < value.length; i++) {
        if (value[i] == '.') {
            measurement.push(value[i])
            float = true
        } else if (true == /\d/.test(value[i])) {
            measurement.push(value[i])
        } else {
            suffix.push(value[i])
        }
    }
    measurement = measurement.join('')
    if (print) {
        console.log(`${f}: The value of the measurement ${value} is ${measurement}`)
    }
    if (float) {
        measurement = parseFloat(measurement)
    } else {
        measurement = parseInt(measurement)
    }
    suffix = suffix.join('')
    if (print) {
        console.log(`${f}: The suffix of the measurement ${value} is ${suffix}`)
    }
    return {
        value: measurement,
        suffix: suffix
    }
}

export const vwToPixels = (vw) => {
    return (vw * window.innerWidth) / 100
}

export const vhToPixels = (vh) => {
    return (vh * window.innerHeight) / 100
}

/**
 * @param {string} value : CSS property value specifying length
 * @param {HTMLElement} context : element providing context for relative measurements
 */
export const getPixelMeasurement = (value, context = null) => {
    const parsed = parseMeasurementString(value)
    switch (parsed.suffix) {
        case 'px':
            return parsed.value
        case 'vh':
            return vhToPixels(parsed.value)
        case 'vmax':
            return Math.max(vwToPixels(parsed.value), vhToPixels(parsed.value))
        case 'vmin':
            return Math.min(vwToPixels(parsed.value), vhToPixels(parsed.value))
        case 'vw':
            return vwToPixels(parsed.value)
        //add new units of measurement here as needed
        default:
            throw new Error(`${f}: Unsupported unit of measurement "${parsed.suffix}"`)
    }
}